import {
  Direction,
  FixMeLater,
  LanguageNameSpaces,
  MarginTypes,
  TimeRange,
} from "@shared/types";
import { createFiltersSlice } from "@shared/slices/filters.slice";
import { TranslationKeys } from "@shared/common/i18n/types";

export enum TradeSourceType {
  GROUP = "Group",
  DCA_BOT = "DCA Bot",
  GRID_BOT = "Grid Bot",
  HEDGING_BOT = "Hedging Bot",
  MANUAL = "Manual",
  TRADING_VIEW = "TradingView Bot",
  SIGNALS_BOT = "Signal Bot",
}

export enum DefaultTimeFrame {
  ALL_TIME = "All Time",
  TODAY = "Today",
  LAST_7_DAYS = "Last 7 Days",
  LAST_30_DAYS = "Last 30 Days",
  THIS_MONTH = "This Month",
  LAST_QUARTER = "Last Quarter",
  LAST_6_MONTHS = "Last 6 Months",
  LAST_YEAR = "Last Year",
}

type RawRangeFilterData = { min: number; max: number };

export interface RawFiltersData {
  button_type: { [key: string]: string };
  headers: FixMeLater;
  values: {
    "Amount Type"?: string[];
    Exchanges?: { [key: string]: { Accounts: string[] } | string[] };
    "Price range"?: number[];
    "Long/Short"?: Direction[];
    Symbols?: string[];
    "Consecutive Active Days": RawRangeFilterData;
    "Source Type"?: { [key in TradeSourceType]: { Source: string[] } };
    "Margin Type"?: string[];
    "Hot Trades Only"?: string[];
    Leverage?: RawRangeFilterData;
    "Time Frame"?: RawRangeFilterData;
    "Average weekly signals range"?: RawRangeFilterData;
    "Price USD range"?: RawRangeFilterData;
    "Trade Type"?: string[];
    "Sub Groups": [
      id: number,
      name: string,
      image: string,
      is_personal: boolean
    ][];
    Status?: string[];
  };
  constants: {
    Accounts?: Record<string, number>;
    Exchanges?: Record<string, number>;
    "Long/Short"?: Record<string, number>;
    "Margin Type"?: Record<string, number>;
    Source?: Record<string, number>;
    "Source New"?: Record<string, Record<string, number>>;
    "Source Type"?: Record<string, number>;
    "Trade Type"?: Record<string, number>;
  };
}

export interface GroupFilterData {
  id: number;
  image: string;
  name: string;
  is_personal?: boolean;
}

export enum Status {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
  INVALID = "Invalid",
  ARCHIVED = "Archived",
}

export enum BacktestingFilterStatus {
  IN_PROGRESS = "In Progress",
  COMPLETED = "Completed",
  FAILED = "Failed",
}

export enum DisplayAmountType {
  TOTAL = "Total",
  AVAILABLE = "Available",
}

export enum TagName {
  NEW = "filters.tags.new",
  TOP_SELLER = "filters.tags.top_seller",
  SALE = "filters.tags.sale",
  FREE = "filters.tags.free",
  FREE_TRIAL = "filters.tags.free_trial",
}

export interface FilterData<T = string[]> {
  options: T;
  value: T;
}

export interface FullFilterData<T = string[]> extends FilterData {
  fullOptions: T;
}

interface BaseFilterDataType<T = string> {
  option: T;
  options: T[];
  value: T[];
}

interface RangeFilterDataType {
  option: undefined;
  options: [min: number, max: number] | [];
  value: [min: number, max: number] | [];
}

interface TimeRangeFilterDataType {
  option: undefined;
  options: [min: number, max: number] | [];
  value: {
    range: [min: number, max: number] | [];
    defaultTimeFrame?: DefaultTimeFrame;
  };
}

interface PriceRangeFilterDataType {
  option: number;
  options: number[];
  value: [min: number, max: number] | [];
}

interface BooleanFilterDataType {
  option: boolean;
  options: undefined;
  value: boolean;
}

export interface FiltersDataTypes {
  [FilterType.EXCHANGES]: BaseFilterDataType;
  [FilterType.SYMBOLS]: BaseFilterDataType;
  [FilterType.CLIENTS]: BaseFilterDataType;
  [FilterType.STATUS]: BaseFilterDataType;
  [FilterType.LONG_SHORT]: BaseFilterDataType;
  [FilterType.SOURCE_NAME]: BaseFilterDataType;
  [FilterType.SOURCE_TYPE]: BaseFilterDataType;
  [FilterType.MARGIN_TYPE]: BaseFilterDataType;
  [FilterType.SIGNAL_STATUS]: BaseFilterDataType<number>;
  [FilterType.FOLLOW_STATUS]: BaseFilterDataType;
  [FilterType.TRADE_TYPE]: BaseFilterDataType;
  [FilterType.LEVERAGE]: RangeFilterDataType;
  [FilterType.CONSECUTIVE_ACTIVE_DAYS]: RangeFilterDataType;
  [FilterType.TIME_FRAME]: TimeRangeFilterDataType;
  [FilterType.GROUPS]: BaseFilterDataType<number>;
  [FilterType.MULTI_GROUPS]: BaseFilterDataType<number>;
  [FilterType.PRICE_RANGE]: PriceRangeFilterDataType;
  [FilterType.IS_HOT]: BooleanFilterDataType;
  [FilterType.VALID_BOT_AMOUNT]: BooleanFilterDataType;
  [FilterType.AVG_NUM_SIGNALS]: RangeFilterDataType;
  [FilterType.AVG_RATING]: BaseFilterDataType<number>;
  [FilterType.USD_PRICE_RANGE]: RangeFilterDataType;
  [FilterType.AMOUNT_TYPE]: BaseFilterDataType;
  [FilterType.TAGS]: BaseFilterDataType;
  [FilterType.BACKTESTING_STATUS]: BaseFilterDataType;
}

export type FilterOption<T extends FilterType> = FiltersDataTypes[T]["option"];
export type FilterOptions<T extends FilterType> =
  FiltersDataTypes[T]["options"];
export type FilterValue<T extends FilterType> = FiltersDataTypes[T]["value"];

export interface RangeFilterData {
  options: [min: number, max: number] | [];
  value: [min: number, max: number] | [];
}

interface TimeFrameFilterData {
  option: number;
  options: number[];
  value: {
    range: [min: number, max: number] | [];
    defaultTimeFrame?: DefaultTimeFrame;
  };
}

export interface PriceRangeFilterData {
  options: number[];
  value: [min: number, max: number] | [];
}

export interface BooleanFilterData {
  value: boolean;
}

export interface FiltersData {
  [FilterType.EXCHANGES]?: FilterData;
  [FilterType.SYMBOLS]?: FilterData;
  [FilterType.CLIENTS]?: FilterData;
  [FilterType.STATUS]?: FilterData;
  [FilterType.LONG_SHORT]?: FilterData;
  [FilterType.SOURCE_NAME]?: FullFilterData;
  [FilterType.SOURCE_TYPE]?: FullFilterData;
  [FilterType.SIGNAL_STATUS]?: FilterData<number[]>;
  [FilterType.FOLLOW_STATUS]?: FilterData;
  [FilterType.MARGIN_TYPE]?: FilterData;
  [FilterType.TRADE_TYPE]?: FilterData;
  [FilterType.LEVERAGE]?: RangeFilterData;
  [FilterType.CONSECUTIVE_ACTIVE_DAYS]?: RangeFilterData;
  [FilterType.TIME_FRAME]?: TimeFrameFilterData;
  [FilterType.GROUPS]?: {
    options: number[];
    value: number[];
    groupIdToData: Record<number, GroupFilterData>;
  };
  [FilterType.MULTI_GROUPS]?: {
    options: number[];
    value: number[];
    groupIdToData: Record<number, GroupFilterData>;
  };
  [FilterType.PRICE_RANGE]?: PriceRangeFilterData;
  [FilterType.USD_PRICE_RANGE]?: PriceRangeFilterData;
  [FilterType.AVG_NUM_SIGNALS]?: PriceRangeFilterData;
  [FilterType.AVG_RATING]?: FilterData<number[]>;
  [FilterType.IS_HOT]?: BooleanFilterData;
  [FilterType.VALID_BOT_AMOUNT]?: BooleanFilterData;
  [FilterType.AMOUNT_TYPE]?: FilterData;
  [FilterType.TAGS]?: FilterData;
  [FilterType.BACKTESTING_STATUS]?: FilterData;
}

export interface FiltersMappings {
  exchangeToClientsMapping?: Record<string, string[] | undefined>;
  clientToExchangeMapping?: Record<string, string>;
  clientToSymbolsMapping?: Record<string, string[]>;
  exchangeToSymbolsMapping?: Record<string, string[]>;
  sourceTypeToSourcesMapping?: Record<string, string[]>;
  sourceToSourceTypesMapping?: Record<string, string[]>;
  sourceIDToSourceMapping?: Record<number, string>;
  sourceToSourceIDsMapping?: Record<string, number[]>;
}
export interface FilterTogglesConfig {
  [FilterToggles.HIDE_DELETED]?: boolean;
}

export type FiltersSliceState = {
  timeRange: TimeRange;
  rawData: RawFiltersData | undefined;
  loadedFilters: boolean;
  loadedFromQuery: boolean;
  config: FilterSliceConfig;
  filters: FiltersData;
  mappings: FiltersMappings;
  loadedArgs: any;
  toggles: FilterTogglesConfig;
};

export interface FilterSliceConfig {
  baseSliceName: string;
  defaultTimeRange?: TimeRange;
  filterTypes?: FilterType[];
  loadedFromQuery?: boolean;
  configTags?: TagName[];
  hideDeletedOptions?: FilterType[];
}

export interface Filters {
  actions: { [key: string]: FixMeLater };
  selectors: { [key: string]: FixMeLater };
}

export enum FilterType {
  IS_HOT,
  EXCHANGES,
  CLIENTS,
  SYMBOLS,
  PRICE_RANGE,
  GROUPS,
  CONSECUTIVE_ACTIVE_DAYS,
  STATUS,
  TIME_FRAME,
  LONG_SHORT,
  MARGIN_TYPE,
  LEVERAGE,
  SOURCE_TYPE,
  SOURCE_NAME,
  TRADE_TYPE,
  VALID_BOT_AMOUNT,
  SIGNAL_STATUS,
  FOLLOW_STATUS,
  USD_PRICE_RANGE,
  AVG_RATING,
  AVG_NUM_SIGNALS,
  AMOUNT_TYPE,
  TAGS,
  BACKTESTING_STATUS,
  MULTI_GROUPS,
}

export const RANGE_FILTER_TYPES = [
  FilterType.LEVERAGE,
  FilterType.TIME_FRAME,
  FilterType.CONSECUTIVE_ACTIVE_DAYS,
  FilterType.AVG_NUM_SIGNALS,
  FilterType.USD_PRICE_RANGE,
] as const;
export type RangeFilterType = (typeof RANGE_FILTER_TYPES)[number];
export const STRING_ARRAY_FILTER_TYPES = [
  FilterType.EXCHANGES,
  FilterType.CLIENTS,
  FilterType.SYMBOLS,
  FilterType.LONG_SHORT,
  FilterType.STATUS,
  FilterType.MARGIN_TYPE,
  FilterType.SOURCE_NAME,
  FilterType.SOURCE_TYPE,
  FilterType.TRADE_TYPE,
  FilterType.FOLLOW_STATUS,
  FilterType.AMOUNT_TYPE,
  FilterType.TAGS,
  FilterType.BACKTESTING_STATUS,
] as const;
export type StringArrayFilterType = (typeof STRING_ARRAY_FILTER_TYPES)[number];
export const CHECKBOX_FILTER_TYPES = [
  ...STRING_ARRAY_FILTER_TYPES,
  FilterType.GROUPS,
  FilterType.MULTI_GROUPS,
  FilterType.SIGNAL_STATUS,
  FilterType.AVG_RATING,
] as const;
export type CheckboxFilterType = (typeof CHECKBOX_FILTER_TYPES)[number];
export const SLIDER_FILTER_TYPES = [
  FilterType.CONSECUTIVE_ACTIVE_DAYS,
  FilterType.LEVERAGE,
  FilterType.AVG_NUM_SIGNALS,
  FilterType.USD_PRICE_RANGE,
] as const;
export type SliderFilterType = (typeof SLIDER_FILTER_TYPES)[number];
export const BOOLEAN_FILTER_TYPES = [
  FilterType.IS_HOT,
  FilterType.VALID_BOT_AMOUNT,
] as const;
export type BooleanFilterType = (typeof BOOLEAN_FILTER_TYPES)[number];

export enum FilterSectionType {
  CHECKBOX,
  RADIO,
  PRICE_RANGE,
  SLIDER,
  MULTI_SELECT,
  TIME_RANGE,
  BOOLEAN,
  TAGS,
}

export type FiltersSlice = ReturnType<typeof createFiltersSlice>;
export type FiltersSelector = FiltersSlice["selectors"];
export type FiltersActions = FiltersSlice["actions"];

export enum LinkedFiltersType {
  FULL_LINK,
  OPTIONS_ONLY,
}

export interface HeaderFilters {
  loadingStatesSelector?: (state: any) => {
    loading: boolean;
    loaded?: boolean;
  };
  loading?: boolean;
  selectors: FiltersSelector;
  actions: FiltersActions;
  hideFilterTypes?: FilterType[];
  filterTypeToTitle?: {
    [key in FilterType]?: TranslationKeys<LanguageNameSpaces.COMMON>;
  };
}

export type SliderFiltersRequest = { min: number | null; max: number | null };
export interface FiltersRequest {
  client_ids?: number[];
  close_timestamp?: SliderFiltersRequest;
  open_timestamp?: SliderFiltersRequest;
  exchanges?: string[];
  margin_type?: MarginTypes;
  multiplier?: SliderFiltersRequest;
  reverse?: boolean;
  source?: Record<number, number[] | undefined>;
  trade_types?: number[];
}

export enum FilterToggles {
  HIDE_DELETED = "Hide Deleted",
}
